/**
 * InvoiceRow Component
 * 
 * Renders a single row in the invoices table.
 * Memoized to prevent unnecessary re-renders when other rows update.
 */
import React, { memo } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faDollarSign, 
  faPen,
  faTrash,
  faCheck,
  faCopy,
  faSearch
} from '@fortawesome/free-solid-svg-icons';

import { Invoice } from 'types';
import { 
  formatAmount, 
  formatDate, 
  getInvoiceStatus,
} from '../../utils/invoiceUtils';

interface InvoiceRowProps {
  invoice: Invoice;
  /** Pre-calculated totals for this invoice */
  calculations: { total: number; tax: number };
  /** Whether this invoice's paid status is being toggled */
  isTogglingPaid: boolean;
  /** Handlers for row actions */
  onRowClick: (id: number, event: React.MouseEvent) => void;
  onViewClick: (id: number) => void;
  onEditClick: (id: number) => void;
  onCloneClick: (invoice: Invoice) => void;
  onTogglePaid: (invoice: Invoice, event: React.MouseEvent) => void;
  onFinalizeClick: (id: number) => void;
  onDeleteClick: (id: number) => void;
}

/**
 * Invoice row component - memoized for performance
 */
const InvoiceRow: React.FC<InvoiceRowProps> = memo(({
  invoice,
  calculations,
  isTogglingPaid,
  onRowClick,
  onViewClick,
  onEditClick,
  onCloneClick,
  onTogglePaid,
  onFinalizeClick,
  onDeleteClick,
}) => {
  return (
    <tr 
      onClick={(e) => onRowClick(invoice.id, e)}
      className="clickable-row"
    >
      {/* Invoice Number with Icon */}
      <td className="cell-number">
        <div className="cell-invoice">
          <button 
            className="invoice-icon-btn"
            onClick={() => onViewClick(invoice.id)}
            title="View Invoice"
          >
            <FontAwesomeIcon icon={faSearch} />
          </button>
          <div className="invoice-details">
            <span className="invoice-number">#{invoice.id}</span>
            <span className="invoice-subtitle">
              {invoice.finalized ? 'Finalized' : 'Draft'}
            </span>
          </div>
        </div>
      </td>

      {/* Customer Names */}
      <td className="cell-customer">{invoice.customer?.first_name || '—'}</td>
      <td className="cell-customer">{invoice.customer?.last_name || '—'}</td>

      {/* Dates */}
      <td className="cell-date text-center">{formatDate(invoice.date)}</td>
      <td className="cell-date text-center">{formatDate(invoice.deadline)}</td>

      {/* Total with Status Dot */}
      <td className="text-right">
        <div className="cell-amount-wrapper">
          <span className="cell-amount">
            {formatAmount(calculations.total)}
          </span>
          <span 
            className={`status-dot ${getInvoiceStatus(invoice.paid, invoice.finalized)}`} 
            title={invoice.paid ? 'Paid' : invoice.finalized ? 'Unpaid' : 'Draft'} 
          />
        </div>
      </td>

      {/* Actions */}
      <td className="cell-actions text-right">
        <div className="action-buttons">
          {/* Toggle Payment Status */}
          <button
            type="button"
            className={`action-btn ${invoice.paid ? 'action-btn-unpaid-dashed' : 'action-btn-paid-filled'}`}
            onClick={(e) => onTogglePaid(invoice, e)}
            disabled={isTogglingPaid}
            title={invoice.paid ? 'Mark as Unpaid' : 'Mark as Paid'}
          >
            <FontAwesomeIcon icon={faDollarSign} />
          </button>

          {/* Clone */}
          <button
            className="action-btn"
            onClick={() => onCloneClick(invoice)}
            title="Clone"
          >
            <FontAwesomeIcon icon={faCopy} />
          </button>

          {/* Edit (only for drafts) */}
          {!invoice.finalized && (
            <button
              className="action-btn"
              onClick={() => onEditClick(invoice.id)}
              title="Edit"
            >
              <FontAwesomeIcon icon={faPen} />
            </button>
          )}

          {/* Finalize (only for drafts) */}
          {!invoice.finalized && (
            <button
              className="action-btn"
              onClick={() => onFinalizeClick(invoice.id)}
              title="Finalize"
            >
              <FontAwesomeIcon icon={faCheck} />
            </button>
          )}

          {/* Delete (only for drafts) */}
          {!invoice.finalized && (
            <button
              className="action-btn"
              onClick={() => onDeleteClick(invoice.id)}
              title="Delete"
              style={{ color: 'var(--color-danger)' }}
            >
              <FontAwesomeIcon icon={faTrash} />
            </button>
          )}
        </div>
      </td>
    </tr>
  );
});

InvoiceRow.displayName = 'InvoiceRow';

export default InvoiceRow;
